import { SketchLineObject, SketchArcObject, SketchEllipseObject, SketchSplineObject, SketchStyleSplineObject, SketchPointObject, SketchPointKind, SketchParabolaObject, SketchEllipseControlPointFactory } from '../../mesh';
import { PointsMaterialFactory, LineMaterialFactory, SketchEdgeBaseStyle, LineMaterialState } from '../../material';
import { Vector2, Vector3 } from 'three';
import type { ReferencePlane } from '../../reference';
import { SKETCH_GEOMETRY_TOLERANCE_CONFIG } from '../config/SketchGeometryToleranceConfig';

/**
 * 等距实体延申处理 —— SketchExtensionAndIntersectionProcessing 的延申部分拆分。
 * 唯一功能: 对多段直线/曲线/圆弧/椭圆弧组合图形执行等距实体时, 对每一段独立偏移后
 *  产生的断开处, 按各实体自身的延申规则补全/延长, 供后续求交与截取使用。
 *
 * 延申方法:
 *  直线 - 沿方向向量无限延伸
 *  椭圆弧 - 满椭圆
 *  圆弧 - 满圆
 *  样条曲线/样式样条曲线 - 沿端点切线方向延长
 *  抛物线 - 沿曲线自身扩展起止参数区间
 */
export class SketchEntityExtension {

    //////////////////////////////////////////////////// 延长   输入SketchXXXObject 输出输入SketchXXXObject

    /**
     * 将线段变成直线 —— 沿方向向量延伸，可通过 direction 控制延伸侧。
     * 
     * 实现说明：
     * 1. 计算 start→end 方向向量，退化时回退到参考面 X 轴；
     * 2. header=true 时起点沿反方向延伸 1000 单位，end=true 时终点沿正方向延伸 1000 单位；
     * 3. 不修改原始 lineObject，创建新的独立 SketchLineObject 返回；
     * 4. 延伸通过直接修改端点坐标实现（isInfiniteLength=false），
     *    端点位置 = 原始位置 + 方向 × (±EXTEND_LENGTH)。
     * 
     * @param lineObject 原始线段对象。
     * @param direction 延伸方向控制：header 控制起点侧，end 控制终点侧。
     * @returns 延伸后的直线对象。
     */
    static sketchExtensionLine(lineObject: SketchLineObject, direction: {
        header: boolean;
        end: boolean;
    }): SketchLineObject {
        // 1. 读取原始线段的逻辑端点位置
        const startPos = lineObject.startPoint.position.clone();
        const endPos = lineObject.endPoint.position.clone();
        const referencePlane = lineObject.referencePlane;
        const isAuxiliary = lineObject.isAuxiliaryObject;

        // 2. 计算线段方向向量
        const dir = endPos.clone().sub(startPos);
        const dirLength = dir.length();

        // 延伸距离（与 SketchLineObject.INFINITE_EXTEND_LENGTH 一致）
        const EXTEND_LENGTH = 1000;

        let newStartPos: Vector3;
        let newEndPos: Vector3;

        if (dirLength < 1e-12) {
            // 退化线段：使用参考面 X 轴作为默认方向
            const fallbackDir = referencePlane.xAxis.clone().normalize();
            newStartPos = direction.header
                ? startPos.clone().addScaledVector(fallbackDir, -EXTEND_LENGTH)
                : startPos.clone();
            newEndPos = direction.end
                ? endPos.clone().addScaledVector(fallbackDir, EXTEND_LENGTH)
                : endPos.clone();
        } else {
            dir.normalize();
            newStartPos = direction.header
                ? startPos.clone().addScaledVector(dir, -EXTEND_LENGTH)
                : startPos.clone();
            newEndPos = direction.end
                ? endPos.clone().addScaledVector(dir, EXTEND_LENGTH)
                : endPos.clone();
        }

        // 3. 创建新的独立端点控制点（位置已按 direction 偏移）
        const newStartPoint = new SketchPointObject({
            position: newStartPos,
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: isAuxiliary,
        });

        const newEndPoint = new SketchPointObject({
            position: newEndPos,
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: isAuxiliary,
        });

        // 4. 创建直线对象（端点坐标已直接控制延伸，不再依赖 isInfiniteLength）
        const extendedLine = new SketchLineObject({
            referencePlane,
            startPoint: newStartPoint,
            endPoint: newEndPoint,
            isInfiniteLength: false,
            isAuxiliaryObject: isAuxiliary,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });

        return extendedLine;
    }

    /**
     * 将圆弧补全为一个满圆 —— 保持圆心、半径和固定端角度不变，延伸端沿弧扫掠方向
     * 绕行 2π 与固定端重合。
     * 
     * 实现说明：
     * 1. 读取原始圆弧的圆心、半径、起止角、方向、参考面；
     * 2. 按 direction 确定延伸端与固定端（与 sketchExtensionLine 的 direction 语义一致）：
     *    - end=true 时终点端为延伸端：startAngle 固定不变，endAngle 沿扫掠方向
     *      绕行 2π 到达 startAngle（clockwise=false 时 endAngle = startAngle + 2π，
     *      clockwise=true 时 endAngle = startAngle - 2π）；
     *    - header=true 时起点端为延伸端：endAngle 固定不变，startAngle 沿扫掠方向
     *      反向绕行 2π 到达 endAngle（clockwise=false 时 startAngle = endAngle - 2π，
     *      clockwise=true 时 startAngle = endAngle + 2π）；
     *    - 两端同时延伸时无固定端，约定按"仅 end=true"处理（固定 startAngle），
     *      等距调用方恒为单端延伸，不会触发该约定分支；
     *    - 两端都不延伸或原始弧跨度已满 2π（已是满圆）时直接克隆返回，
     *      避免产生 4π 跨度的非法角度表示；
     * 3. 满圆下起止角相差 2π，起止点均位于固定端角度对应的圆周位置，
     *    在世界坐标中通过参考面 localToWorld 计算，起止控制点精确重合；
     * 4. 不修改原始 arcObject，创建新的独立 SketchArcObject 返回；
     * 5. 满圆在 SketchArcObject 中天然支持 —— THREE.EllipseCurve 将 ±2π 跨度
     *    规范化为完整扫掠，updateGeometry() 按起止角采样即完整圆。
     * 
     * @param arcObject 原始圆弧对象。
     * @param direction 延伸方向控制：header 控制起点侧，end 控制终点侧。
     * @returns 补全后的满圆对象（SketchArcObject）。
     */
    static sketchExtensionArc(arcObject: SketchArcObject, direction: {
        header: boolean;
        end: boolean;
    }): SketchArcObject {
        // 1. 读取原始圆弧的定义
        const definition = arcObject.getArcDefinition();
        const { centerWorld, radius, startAngle, endAngle, clockwise, segments = 64 } = definition;
        const referencePlane = arcObject.referencePlane;

        // 2. 防护：两端都不延伸或原始弧已是满圆（跨度 ≥ 2π）时直接克隆返回
        //    满圆再延伸会产生 ±4π 跨度表示：THREE.EllipseCurve 虽仍渲染满圆，
        //    但下游的跨度判定（如 SketchEllipseObject.computeEllipseSweep）会
        //    把 4π 误判为部分椭圆，故必须在此拦截
        const fullSweep = Math.PI * 2;
        const isFullSweep = Math.abs(endAngle - startAngle)
            >= fullSweep - SKETCH_GEOMETRY_TOLERANCE_CONFIG.fullSweepAngle;
        if ((!direction.header && !direction.end) || isFullSweep) {
            return arcObject.cloneSelf();
        }

        // 3. 计算补全后的起止角
        //    弧扫掠方向符号：逆时针（clockwise=false）为角度增大方向 +1，
        //    顺时针为角度减小方向 -1；延伸端沿扫掠方向绕行 2π 与固定端重合：
        //    - end=true：固定 startAngle，endAngle 沿扫掠方向走满 2π
        //      （优先于 header，两端同时延伸时无固定端，按此约定处理）；
        //    - 否则（header=true）：固定 endAngle，startAngle 沿扫掠方向反向绕行 2π
        const spanSign = clockwise ? -1 : 1;
        const nextStartAngle = direction.end ? startAngle : endAngle - spanSign * fullSweep;
        const nextEndAngle = direction.end ? startAngle + spanSign * fullSweep : endAngle;

        // 4. 计算起止点世界坐标（满圆下起止角相差 2π，对应圆周上同一点 —— 固定端位置）
        const localCenter = referencePlane.worldToLocal(centerWorld.clone());
        const computeArcEndpointWorld = (angle: number): Vector3 => {
            const localPoint = new Vector2(
                localCenter.x + radius * Math.cos(angle),
                localCenter.y + radius * Math.sin(angle),
            );
            return referencePlane.localToWorld(localPoint);
        };
        const fixedEndWorld = computeArcEndpointWorld(direction.end ? startAngle : endAngle);

        // 5. 创建起止控制点（位置重合于固定端）
        const newStartPoint = new SketchPointObject({
            position: fixedEndWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
        });

        const newEndPoint = new SketchPointObject({
            position: fixedEndWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
        });

        // 6. 创建圆心控制点
        const newCenterPoint = new SketchPointObject({
            position: centerWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        // 7. 构造满圆对象（固定端角度不变，延伸端角度与其相差 2π，clockwise 保持原值）
        const fullCircle = new SketchArcObject({
            referencePlane,
            definition: {
                centerWorld: centerWorld.clone(),
                radius,
                startAngle: nextStartAngle,
                endAngle: nextEndAngle,
                clockwise,
                segments,
            },
            startPoint: newStartPoint,
            endPoint: newEndPoint,
            centerPoint: newCenterPoint,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });

        return fullCircle;
    }

    /**
     * 将椭圆弧补全为一个满椭圆 —— 保持圆心、半轴、旋转角和固定端角度不变，
     * 延伸端沿弧扫掠方向绕行 2π 与固定端重合。
     * 
     * 实现说明：
     * 1. 读取原始椭圆弧的圆心、rx、ry、旋转角、起止角、方向、采样段数；
     * 2. 与 sketchExtensionArc 同一套延伸语义：
     *    - end=true 时终点端为延伸端：startAngle 固定不变，endAngle 沿扫掠方向
     *      绕行 2π 到达 startAngle（clockwise=false 时 endAngle = startAngle + 2π，
     *      clockwise=true 时 endAngle = startAngle - 2π）；
     *    - header=true 时起点端为延伸端：endAngle 固定不变，startAngle 沿扫掠方向
     *      反向绕行 2π 到达 endAngle（clockwise=false 时 startAngle = endAngle - 2π，
     *      clockwise=true 时 startAngle = endAngle + 2π）；
     *    - 两端同时延伸时约定按"仅 end=true"处理（固定 startAngle）；
     *    - 两端都不延伸或原始椭圆弧跨度已满 2π（已是满椭圆）时直接克隆返回，
     *      避免 4π 跨度表示导致 isPartialEllipseDefinition 误判为部分椭圆；
     * 3. 起止控制点均位于固定端参数角对应的椭圆位置：按参数角在局部坐标系
     *    计算端点，再绕局部圆心旋转 rotation 后映射回世界坐标
     *    （与 SketchEllipseObject.resolveWorldPointByAngle 口径一致）；
     * 4. 不修改原始 ellipseObject，创建新的独立 SketchEllipseObject 返回。
     * 
     * @param ellipseObject 原始椭圆弧对象。
     * @param direction 延伸方向控制：header 控制起点侧，end 控制终点侧。
     * @returns 补全后的满椭圆对象（SketchEllipseObject）。
     */
    static sketchExtensionEllipse(ellipseObject: SketchEllipseObject, direction: {
        header: boolean;
        end: boolean;
    }): SketchEllipseObject {
        // 1. 读取原始椭圆弧的定义
        const definition = ellipseObject.getEllipseDefinition();
        const {
            centerWorld, radiusX, radiusY, rotation,
            startAngle, endAngle, clockwise, segments = 64,
        } = definition;
        const referencePlane = ellipseObject.referencePlane;

        // 2. 防护：两端都不延伸或原始椭圆弧已是满椭圆（跨度 ≥ 2π）时直接克隆返回
        //    满椭圆再延伸会产生 ±4π 跨度表示，computeEllipseSweep 会返回 4π，
        //    isPartialEllipseDefinition 将其误判为部分椭圆，故必须在此拦截
        const fullSweep = Math.PI * 2;
        const isFullSweep = Math.abs(endAngle - startAngle)
            >= fullSweep - SKETCH_GEOMETRY_TOLERANCE_CONFIG.fullSweepAngle;
        if ((!direction.header && !direction.end) || isFullSweep) {
            return ellipseObject.cloneSelf();
        }

        // 3. 计算补全后的起止参数角（与 sketchExtensionArc 同一套延伸语义）
        //    椭圆参数角与 THREE.EllipseCurve 语义一致，扫掠方向符号由 clockwise 决定
        const spanSign = clockwise ? -1 : 1;
        const nextStartAngle = direction.end ? startAngle : endAngle - spanSign * fullSweep;
        const nextEndAngle = direction.end ? startAngle + spanSign * fullSweep : endAngle;

        // 4. 计算起止点世界坐标（满椭圆下起止参数角相差 2π，对应同一点 —— 固定端位置）
        const localCenter = referencePlane.worldToLocal(centerWorld.clone());
        const computeEllipseEndpointWorld = (angle: number): Vector3 => {
            const localPoint = new Vector2(
                localCenter.x + radiusX * Math.cos(angle),
                localCenter.y + radiusY * Math.sin(angle),
            ).rotateAround(localCenter, rotation);
            return referencePlane.localToWorld(localPoint);
        };
        const fixedEndWorld = computeEllipseEndpointWorld(direction.end ? startAngle : endAngle);

        // 5. 创建起止控制点（位置重合于固定端）
        const fullEllipseStartPoint = new SketchPointObject({
            position: fixedEndWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        const fullEllipseEndPoint = new SketchPointObject({
            position: fixedEndWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        const fullEllipseCenterPoint = new SketchPointObject({
            position: centerWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        // 6. 构造满椭圆对象（固定端角度不变，延伸端角度与其相差 2π，clockwise 保持原值）
        const fullEllipse = new SketchEllipseObject({
            referencePlane,
            definition: {
                centerWorld: centerWorld.clone(),
                radiusX,
                radiusY,
                rotation,
                startAngle: nextStartAngle,
                endAngle: nextEndAngle,
                clockwise,
                segments,
            },
            startPoint: fullEllipseStartPoint,
            endPoint: fullEllipseEndPoint,
            centerPoint: fullEllipseCenterPoint,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });

        // 7. 与正常绘制/等距偏移同一口径注册基准轴控制点：
        //    延伸产物是全新实例，若不补齐注册，落地阶段创建 curve-offset-distance
        //    尺寸时取不到 MajorPositive 标记点（尺寸被静默跳过），等距圆心重合
        //    关系也会因圆心点未进入草图组而丢失
        const controlPointFactory = new SketchEllipseControlPointFactory(referencePlane);
        controlPointFactory.registerCardinalControlPoints(fullEllipse, {
            centerPoint: fullEllipseCenterPoint,
            axisControlPoints: controlPointFactory.createAxisControlPoints({
                centerWorld: centerWorld.clone(),
                radiusX,
                radiusY,
                rotation,
            }),
        });

        return fullEllipse;
    }

    /**
     * 延申样条曲线 —— 沿端点切线方向向两端各延伸 INFINITE_EXTEND_LENGTH（1000 单位）。
     * 
     * 实现说明：
     * 1. 取原始 curvePoints，通过 3 点二次拟合计算起止端点切线方向；
     * 2. 沿切线反方向在起点前插入延伸点，沿切线正方向在终点后追加延伸点；
     * 3. 对原始起点/终点附近各 3 个曲线点做余弦衰减平滑过渡；
     * 4. 控制点（含起止点）保持原始逻辑位置不变，
     *    仅 curvePoints 被扩展，几何渲染按扩展后的点列生成；
     * 5. 不修改原始 splineObject，创建新的独立 SketchSplineObject 返回；
     * 6. 闭合样条不进行延伸（首尾相连无切线延伸出口）。
     * 
     * @param splineObject 原始样条曲线对象。
     * @returns 延伸后的样条曲线对象。
     */
    static sketchExtensionSpline(splineObject: SketchSplineObject, direction: {
        header: boolean;
        end: boolean;
    }): SketchSplineObject {
        // 1. 获取原始数据
        const curvePoints = splineObject.getCurvePoints();
        const referencePlane = splineObject.referencePlane;
        const curveMetadata = splineObject.getCurveMetadata();
        const fullControlPoints = splineObject.getControlPoints(); // [start, ...interior, end]

        // 闭合样条或两端都不延伸时直接克隆返回
        if (curveMetadata.closureKind === 'closed' || curvePoints.length < 2
            || (!direction.header && !direction.end)) {
            return splineObject.cloneSelf();
        }

        // 2. 计算 1000 单位延伸距离（与 SketchLineObject.INFINITE_EXTEND_LENGTH 一致）
        const EXTEND_LENGTH = 1000;

        // 3. 按 direction 计算起点侧延伸向量（仅 header=true 时延伸）
        let startExtendDir: Vector3 | null = null;
        if (direction.header) {
            const startTangent = SketchEntityExtension.computeEndpointTangent(
                curvePoints, 'start',
            );
            startExtendDir = startTangent.clone().normalize().multiplyScalar(-EXTEND_LENGTH);
        }

        // 4. 按 direction 计算终点侧延伸向量（仅 end=true 时延伸）
        let endExtendDir: Vector3 | null = null;
        if (direction.end) {
            const endTangent = SketchEntityExtension.computeEndpointTangent(
                curvePoints, 'end',
            );
            endExtendDir = endTangent.clone().normalize().multiplyScalar(EXTEND_LENGTH);
        }

        // 5. 构建扩展后的曲线点列
        const extendedPoints: Vector3[] = [];
        const pointCount = curvePoints.length;
        const blendLength = Math.min(3, pointCount - 1); // 平滑过渡的邻近点数

        // 起点延伸点（仅 header=true）
        if (startExtendDir) {
            const firstPoint = curvePoints[0]!.clone();
            extendedPoints.push(firstPoint.clone().add(startExtendDir));
        }

        // 原始曲线点 —— 对需要延伸的端点侧做余弦衰减平滑
        for (let i = 0; i < pointCount; i++) {
            const originalPoint = curvePoints[i]!.clone();

            if (startExtendDir && i <= blendLength) {
                // 起点侧余弦衰减
                const blendFactor = Math.cos((i / Math.max(1, blendLength)) * (Math.PI / 2));
                const delta = startExtendDir.clone().multiplyScalar(blendFactor);
                extendedPoints.push(originalPoint.add(delta));
            } else if (endExtendDir && i >= pointCount - 1 - blendLength) {
                // 终点侧余弦衰减
                const reverseIndex = pointCount - 1 - i;
                const blendFactor = Math.cos((reverseIndex / Math.max(1, blendLength)) * (Math.PI / 2));
                const delta = endExtendDir.clone().multiplyScalar(blendFactor);
                extendedPoints.push(originalPoint.add(delta));
            } else {
                // 中间点保持不变（或非延伸侧保持不变）
                extendedPoints.push(originalPoint);
            }
        }

        // 终点延伸点（仅 end=true）
        if (endExtendDir) {
            const lastPoint = curvePoints[pointCount - 1]!.clone();
            extendedPoints.push(lastPoint.clone().add(endExtendDir));
        }

        // 6. 克隆控制点（中间控制点，不含起止）
        const interiorControlPoints = fullControlPoints.slice(1, -1);
        const clonedInteriorPoints = interiorControlPoints.map((cp) =>
            new SketchPointObject({
                position: cp.position.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane,
            }),
        );

        // 7. 创建起止控制点（保持原始位置）
        const newStartPoint = new SketchPointObject({
            position: fullControlPoints[0]!.position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        const newEndPoint = new SketchPointObject({
            position: fullControlPoints[fullControlPoints.length - 1]!.position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        // 8. 构造延伸后的样条曲线对象
        const extendedSpline = new SketchSplineObject({
            referencePlane,
            startPoint: newStartPoint,
            endPoint: newEndPoint,
            controlPoints: clonedInteriorPoints,
            curvePoints: extendedPoints,
            curveMetadata,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });

        return extendedSpline;
    }

    /**
     * 延申样式样条曲线 —— 沿端点切线方向向两端各延伸 INFINITE_EXTEND_LENGTH（1000 单位）。
     * 
     * 实现说明：
     * 1. 与 {@link sketchExtensionSpline} 逻辑完全一致，仅出入类型为 SketchStyleSplineObject；
     * 2. 复用 {@link computeEndpointTangent} 计算端点切线；
     * 3. 闭合样式样条直接 cloneSelf 返回。
     * 
     * @param styleSplineObject 原始样式样条曲线对象。
     * @returns 延伸后的样式样条曲线对象。
     */
    static sketchExtensionStypeSpline(styleSplineObject: SketchStyleSplineObject, direction: {
        header: boolean;
        end: boolean;
    }): SketchStyleSplineObject {
        // 1. 获取原始数据
        const curvePoints = styleSplineObject.getCurvePoints();
        const referencePlane = styleSplineObject.referencePlane;
        const curveMetadata = styleSplineObject.getCurveMetadata();
        const fullControlPoints = styleSplineObject.getControlPoints(); // [start, ...interior, end]

        // 闭合样式样条或两端都不延伸时直接克隆返回
        if (curveMetadata.closureKind === 'closed' || curvePoints.length < 2
            || (!direction.header && !direction.end)) {
            return styleSplineObject.cloneSelf();
        }

        // 2. 计算 1000 单位延伸距离（与 SketchLineObject.INFINITE_EXTEND_LENGTH 一致）
        const EXTEND_LENGTH = 1000;

        // 3. 按 direction 计算起点侧延伸向量（仅 header=true 时延伸）
        let startExtendDir: Vector3 | null = null;
        if (direction.header) {
            const startTangent = SketchEntityExtension.computeEndpointTangent(
                curvePoints, 'start',
            );
            startExtendDir = startTangent.clone().normalize().multiplyScalar(-EXTEND_LENGTH);
        }

        // 4. 按 direction 计算终点侧延伸向量（仅 end=true 时延伸）
        let endExtendDir: Vector3 | null = null;
        if (direction.end) {
            const endTangent = SketchEntityExtension.computeEndpointTangent(
                curvePoints, 'end',
            );
            endExtendDir = endTangent.clone().normalize().multiplyScalar(EXTEND_LENGTH);
        }

        // 5. 构建扩展后的曲线点列
        const extendedPoints: Vector3[] = [];
        const pointCount = curvePoints.length;
        const blendLength = Math.min(3, pointCount - 1);

        // 起点延伸点（仅 header=true）
        if (startExtendDir) {
            const firstPoint = curvePoints[0]!.clone();
            extendedPoints.push(firstPoint.clone().add(startExtendDir));
        }

        // 原始曲线点 —— 对需要延伸的端点侧做余弦衰减平滑
        for (let i = 0; i < pointCount; i++) {
            const originalPoint = curvePoints[i]!.clone();

            if (startExtendDir && i <= blendLength) {
                const blendFactor = Math.cos((i / Math.max(1, blendLength)) * (Math.PI / 2));
                extendedPoints.push(originalPoint.add(startExtendDir.clone().multiplyScalar(blendFactor)));
            } else if (endExtendDir && i >= pointCount - 1 - blendLength) {
                const reverseIndex = pointCount - 1 - i;
                const blendFactor = Math.cos((reverseIndex / Math.max(1, blendLength)) * (Math.PI / 2));
                extendedPoints.push(originalPoint.add(endExtendDir.clone().multiplyScalar(blendFactor)));
            } else {
                extendedPoints.push(originalPoint);
            }
        }

        // 终点延伸点（仅 end=true）
        if (endExtendDir) {
            const lastPoint = curvePoints[pointCount - 1]!.clone();
            extendedPoints.push(lastPoint.clone().add(endExtendDir));
        }

        // 6. 克隆控制点（中间控制点，不含起止）
        const interiorControlPoints = fullControlPoints.slice(1, -1);
        const clonedInteriorPoints = interiorControlPoints.map((cp) =>
            new SketchPointObject({
                position: cp.position.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
                referencePlane,
            }),
        );

        // 7. 创建起止控制点（保持原始位置）
        const newStartPoint = new SketchPointObject({
            position: fullControlPoints[0]!.position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        const newEndPoint = new SketchPointObject({
            position: fullControlPoints[fullControlPoints.length - 1]!.position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        // 8. 构造延伸后的样式样条曲线对象
        const extendedSpline = new SketchStyleSplineObject({
            referencePlane,
            startPoint: newStartPoint,
            endPoint: newEndPoint,
            controlPoints: clonedInteriorPoints,
            curvePoints: extendedPoints,
            curveMetadata,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });

        return extendedSpline;
    }

    /**
     * 通过 3 点二次拟合计算样条曲线端点切线方向。
     * 
     * 设计说明：
     * 1. 点数 ≥ 3 时使用二次拟合（O(h²) 精度）：
     *    起点：f'(0) = (4*P1 - 3*P0 - P2) / 2
     *    终点：f'(n) = (3*P2 + P0 - 4*P1) / 2
     * 2. 点数 = 2 时退化为弦线方向（统一取 last - first，即曲线前进方向，
     *    与 3 点分支"切线总是指向曲线前进方向"的语义一致，供调用方按侧向符号延伸）；
     * 3. 切线大小为 0 时回退到参考面 X 轴方向。
     * 
     * @param curvePoints 曲线采样点列表。
     * @param side 取起点侧还是终点侧（仅影响 3 点分支的拟合公式，不影响方向语义）。
     * @returns 切线向量（未归一化）。
     */
    private static computeEndpointTangent(
        curvePoints: Vector3[],
        side: 'start' | 'end',
    ): Vector3 {
        const count = curvePoints.length;

        if (count >= 3) {
            const p0 = curvePoints[side === 'start' ? 0 : count - 3]!;
            const p1 = curvePoints[side === 'start' ? 1 : count - 2]!;
            const p2 = curvePoints[side === 'start' ? 2 : count - 1]!;

            if (side === 'start') {
                // f'(0) = (4*P1 - 3*P0 - P2) / 2
                return new Vector3(
                    (4 * p1.x - 3 * p0.x - p2.x) / 2,
                    (4 * p1.y - 3 * p0.y - p2.y) / 2,
                    (4 * p1.z - 3 * p0.z - p2.z) / 2,
                );
            }
            // 终点：f'(n) = (3*P2 + P0 - 4*P1) / 2
            return new Vector3(
                (3 * p2.x + p0.x - 4 * p1.x) / 2,
                (3 * p2.y + p0.y - 4 * p1.y) / 2,
                (3 * p2.z + p0.z - 4 * p1.z) / 2,
            );
        }

        // 点数 = 2：弦线方向
        // 注意：无论取起点侧还是终点侧，切线都应指向曲线前进方向（P1 - P0），
        // 与 3 点分支的语义保持一致 —— 调用方据此决定向曲线外侧延伸的符号
        // （start 侧乘 -1、end 侧乘 +1）。若 end 侧错误地取 P0 - P1，
        // 终点切线将指向曲线内部，导致延伸方向反转。
        const first = curvePoints[0]!;
        const last = curvePoints[count - 1]!;
        const chord = new Vector3().subVectors(last, first);

        const length = chord.length();
        if (length < 1e-12) {
            return new Vector3(1, 0, 0);
        }
        return chord;
    }

    /**
     * 将抛物线沿曲线自身向指定侧延长 —— 保持焦点、顶点、参考面不变，扩展起止参数区间。
     * 
     * 实现说明：
     * 1. 抛物线由「焦点 + 顶点」唯一确定，是无界开放曲线，不存在"补全为闭合图形"的概念，
     *    因此补全语义为沿曲线自身精确延长（与圆弧补全为满圆、椭圆弧补全为满椭圆不同）；
     * 2. 抛物线参数为沿侧向轴的坐标，任意实参对应的点均精确落在曲线上
     *    （见 SketchParabolaObject.resolveWorldPointByParameter），
     *    故直接对参数增量扩展即可实现精确延长，无需采样/拟合；
     * 3. 按 direction 单侧延长：header=true 时向起点外侧延长，end=true 时向终点外侧延长；
     *    延长方向由参数区间走向决定：正向区间（end ≥ start）时起点侧为参数减小方向、
     *    终点侧为参数增大方向；端点反向越过（end < start，SolidWorks 允许拖动端点越过对方）时
     *    起点侧为参数增大方向、终点侧为参数减小方向，与
     *    {@link sketchExtensionLine}/{@link sketchExtensionSpline} 的 direction 语义一致；
     * 4. 两端都不延长时直接克隆返回（与样条延伸行为一致）；
     * 5. 不修改原始 parabolaObject：cloneSelf 复制材质/约束/显示状态，
     *    updateParabolaDefinition 重算几何并将起止控制点回贴到新参数对应的精确曲线位置。
     * 
     * @param parabolaObject 原始抛物线对象。
     * @param direction 延伸方向控制：header 控制起点侧，end 控制终点侧。
     * @returns 延长后的抛物线对象。
     */
    static sketchExtensionParabola(parabolaObject: SketchParabolaObject, direction: {
        header: boolean;
        end: boolean;
    }): SketchParabolaObject {
        // 1. 读取原始抛物线定义
        const definition = parabolaObject.getParabolaDefinition();

        // 延伸距离（与 SketchLineObject.INFINITE_EXTEND_LENGTH 一致）
        const EXTEND_LENGTH = 1000;

        // 两端都不延伸时直接克隆返回
        if (!direction.header && !direction.end) {
            return parabolaObject.cloneSelf();
        }

        // 2. 按 direction 扩展起止参数区间
        //    抛物线参数为沿侧向轴的坐标，参数增减即沿曲线自身精确移动；
        //    注意：参数区间可能反向（start > end，端点拖动越过对方），此时起点位于区间右端、
        //    终点位于区间左端，向曲线外延伸的符号与正向区间相反，
        //    故先按区间走向（spanDirection）归一延长方向，避免把端点往段内收缩
        const spanDirection = definition.endParameter >= definition.startParameter ? 1 : -1;
        const nextDefinition = {
            ...definition,
            startParameter: direction.header
                ? definition.startParameter - spanDirection * EXTEND_LENGTH
                : definition.startParameter,
            endParameter: direction.end
                ? definition.endParameter + spanDirection * EXTEND_LENGTH
                : definition.endParameter,
        };

        // 3. 克隆原对象并更新定义，得到独立延长的抛物线
        const extendedParabola = parabolaObject.cloneSelf();
        extendedParabola.updateParabolaDefinition(nextDefinition);

        return extendedParabola;
    }
}
